/**
 * Health Plan Builder — handle-based recommendation engine + UI
 */
(function () {
  'use strict';

  const PLAN_INCLUDED_LIMITS = { minimal: 2, comprehensive: 4 };
  const DISPLAY_MAX = 4;
  const BUNDLE_DISPLAY_MAX = 1;

  const FORMAT_FILTER_OPTIONS = [
    { value: 'capsules', label: 'Capsules' },
    { value: 'powder', label: 'Powder' },
    { value: 'liquid', label: 'Liquid' },
    { value: 'gummies', label: 'Gummies' },
  ];

  const CHEVRON_DOWN_SVG =
    '<svg class="theme-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true"><path stroke="currentColor" d="M1.5 7 12 17.5 22.5 7" vector-effect="non-scaling-stroke"/></svg>';

  const EXCLUDED_HANDLES = new Set([
    'the-frother',
    'bull-denim-essential-tote-bag',
    'glass-measuring-cup',
    'navidium-shipping-protection',
    'cnpusa-gift-card',
    'brighter-shine-8oz-natural-dishwasher-additive-hard-water-cleaner',
  ]);

  const SCORE = {
    ORDER_WEIGHT: 1000,
    MULTI_GOAL: 50,
    PRIMARY_BONUS: 30,
    BUNDLE_BONUS: 20,
  };

  const ITEM_LIST_NAME = 'health_plan_builder';
  const ATTR_SOURCE_VALUE = 'health_plan_builder';
  const INCLUDED_STORAGE_KEY = 'hpb_included_products';

  /**
   * Analytics helper. Pushes unprefixed events to window.dataLayer (legacy /
   * theme-side) and also publishes prefixed custom events via
   * Shopify.analytics.publish so the sandboxed FeedArmy GTM custom pixel can
   * subscribe. Does NOT call gtag directly and does NOT reuse the Triangle
   * Digital ecommerceDataLayer contract.
   */
  const HpbAnalytics = {
    push(event, payload) {
      const data = payload || {};
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event }, data));
      } catch (err) {
        /* analytics must never break the builder */
      }
      this.publishToShopify(event, data);
    },

    /**
     * Bridge to Shopify Customer Events so GTM-PG86R8DT (FeedArmy custom pixel)
     * can receive builder events. Retries briefly if Shopify.analytics is not
     * ready yet (common for builder_start on first paint).
     */
    publishToShopify(event, payload, attempt) {
      const tryCount = attempt || 0;
      try {
        if (
          window.Shopify &&
          window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function'
        ) {
          window.Shopify.analytics.publish('hpb:' + event, payload || {});
          return;
        }
      } catch (err) {
        return;
      }
      if (tryCount >= 20) return;
      setTimeout(() => {
        this.publishToShopify(event, payload, tryCount + 1);
      }, 100);
    },

    goalSlug(goal) {
      if (!goal) return '';
      const label = typeof goal === 'string' ? goal : goal.label || '';
      return label
        .toLowerCase()
        .trim()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },

    goalSlugList(goals) {
      return goals.map((g) => this.goalSlug(g)).filter(Boolean);
    },

    getAttribution() {
      const attribution = {
        entry_source: 'direct',
        utm_source: '',
        utm_medium: '',
        utm_campaign: '',
      };

      try {
        const params = new URLSearchParams(window.location.search);
        attribution.utm_source = params.get('utm_source') || '';
        attribution.utm_medium = params.get('utm_medium') || '';
        attribution.utm_campaign = params.get('utm_campaign') || '';

        if (attribution.utm_source) {
          attribution.entry_source = attribution.utm_source;
        } else if (document.referrer) {
          const referrerHost = new URL(document.referrer).hostname;
          if (referrerHost && referrerHost !== window.location.hostname) {
            attribution.entry_source = referrerHost;
          }
        }
      } catch (err) {
        /* leave defaults */
      }

      return attribution;
    },
  };

  function dedupeProducts(products) {
    const map = new Map();
    (products || []).forEach((product) => {
      if (product && product.handle) {
        map.set(product.handle.toLowerCase(), product);
      }
    });
    return Array.from(map.values());
  }

  function parseHandles(text) {
    if (!text) return [];
    return text
      .split(/[\n,]+/)
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean);
  }

  function normalizeHandles(value) {
    if (Array.isArray(value)) {
      return value.map((h) => String(h).trim().toLowerCase()).filter(Boolean);
    }
    return parseHandles(value);
  }

  function isPetProduct(product) {
    return product.vendor === 'CNP Pet';
  }

  function isTestingKit(product) {
    return (product.tags || []).some((t) => t.toLowerCase() === 'test');
  }

  function isProductAvailable(product) {
    if (product.variants?.length) {
      return product.variants.some((v) => v.available !== false);
    }
    return product.available !== false;
  }

  function getGoalTestingKeywords(goal) {
    const label = goal.label.toLowerCase();
    const keywords = new Set();

    const tagHints = [
      'kidney',
      'urinary',
      'liver',
      'gallbladder',
      'digestion',
      'reflux',
      'heartburn',
      'joint',
      'mobility',
      'uric',
      'heart',
      'circulation',
      'prostate',
      'sleep',
      'stress',
      'energy',
      'fitness',
      'stonebreaker',
    ];

    tagHints.forEach((hint) => {
      if (label.includes(hint)) keywords.add(hint);
    });

    if (goal.is_digestion_goal) {
      keywords.add('digestion');
      keywords.add('acid reflux');
    }

    return Array.from(keywords);
  }

  function testingKitMatchesGoal(product, goal) {
    const productTags = (product.tags || []).map((t) => t.toLowerCase());
    const keywords = getGoalTestingKeywords(goal);

    return keywords.some((keyword) =>
      productTags.some(
        (tag) => tag.includes(keyword) || keyword.includes(tag)
      )
    );
  }

  function isExcluded(product) {
    return EXCLUDED_HANDLES.has(product.handle);
  }

  function isGlassBottle(product) {
    return (product.handle || '').toLowerCase().includes('glass-bottle');
  }

  function isBundle(product) {
    return (product.tags || []).some((t) => t.toLowerCase() === 'bundle');
  }

  function getBundleComponentHandles(product) {
    return (product.bundle_component_handles || [])
      .map((h) => String(h).trim().toLowerCase())
      .filter(Boolean);
  }

  function isBundleProduct(product) {
    return isBundle(product) || getBundleComponentHandles(product).length > 0;
  }

  function getProductFormat(product) {
    const tags = (product.tags || []).map((t) => t.toLowerCase());
    if (tags.includes('bundle')) return 'bundle';
    if (tags.includes('capsules')) return 'capsules';
    if (tags.includes('powder')) return 'powder';
    if (tags.includes('liquid')) return 'liquid';
    if (tags.includes('gummies')) return 'gummies';
    return null;
  }

  const FAMILY_STRIP_TOKENS = new Set([
    'organic',
    'original',
    'capsules',
    'capsule',
    'powder',
    'powders',
    'liquid',
    'concentrate',
    'gummies',
    'gummy',
    'granular',
    'bag',
    'glass',
    'bottle',
    'oz',
    'softgels',
    'softgel',
    'tablets',
    'tablet',
  ]);

  function getProductFamily(product) {
    const stem = (product.handle || '')
      .toLowerCase()
      .split('-')
      .filter((t) => t && !FAMILY_STRIP_TOKENS.has(t) && !/^\d+(oz)?$/.test(t))
      .join('-');
    return stem || (product.handle || '').toLowerCase();
  }

  function dedupeByFamily(scoredSorted) {
    const seen = new Set();
    return scoredSorted.filter((p) => {
      const key = getProductFamily(p);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function formatMoney(cents) {
    if (typeof window.theme !== 'undefined' && window.theme.moneyFormat) {
      const fmt = window.theme.moneyFormat;
      const amount = (cents / 100).toFixed(2);
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function formatGoalList(labels) {
    if (labels.length <= 1) return labels[0] || '';
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`;
  }

  function buildStackSummary(selectedGoalIds, goals) {
    const labels = selectedGoalIds
      .map((id) => goals.find((g) => g.id === id)?.label)
      .filter(Boolean);
    if (!labels.length) return '';

    const primary = labels[0];
    const others = labels.slice(1);
    if (!others.length) {
      return `Built around ${primary}.`;
    }
    return `Built around ${primary}, with support for ${formatGoalList(others)}.`;
  }

  function getGoalHandles(goal, digestionBranch) {
    if (goal.is_digestion_goal && digestionBranch) {
      return digestionBranch === 'reflux'
        ? goal.reflux_handles
        : goal.general_digestion_handles;
    }
    return goal.product_handles;
  }

  function buildHandleRankMap(goals, selectedGoalIds, primaryGoalId, digestionBranch) {
    const rankMap = new Map();

    selectedGoalIds.forEach((goalId) => {
      const goal = goals.find((g) => g.id === goalId);
      if (!goal) return;

      const handles = getGoalHandles(goal, digestionBranch);
      handles.forEach((handle, index) => {
        const key = handle.toLowerCase();
        if (!rankMap.has(key)) {
          rankMap.set(key, { goalIds: [], bestRank: Infinity });
        }
        const entry = rankMap.get(key);
        if (!entry.goalIds.includes(goalId)) entry.goalIds.push(goalId);
        entry.bestRank = Math.min(entry.bestRank, index);
      });
    });

    return rankMap;
  }

  function scoreProduct(product, rankMap, primaryGoalId) {
    const entry = rankMap.get(product.handle.toLowerCase());
    if (!entry) return null;

    let score = SCORE.ORDER_WEIGHT - entry.bestRank;
    if (entry.goalIds.length > 1) score += SCORE.MULTI_GOAL * (entry.goalIds.length - 1);
    if (primaryGoalId && entry.goalIds.includes(primaryGoalId)) score += SCORE.PRIMARY_BONUS;
    if (isBundle(product)) score += SCORE.BUNDLE_BONUS;

    return {
      ...product,
      score,
      matchedGoalIds: entry.goalIds,
      isBundle: isBundle(product),
    };
  }

  function getScoredCandidates(products, goals, state, applyFormatFilters) {
    const { selectedGoalIds, formatFilters, digestionBranch } = state;
    if (!selectedGoalIds.length) return null;

    const digestionGoal = goals.find((g) => g.is_digestion_goal);
    if (
      digestionGoal &&
      selectedGoalIds.includes(digestionGoal.id) &&
      !digestionBranch
    ) {
      return null;
    }

    const primaryGoalId = selectedGoalIds[0];
    const rankMap = buildHandleRankMap(
      goals,
      selectedGoalIds,
      primaryGoalId,
      digestionBranch
    );

    let filtered = products.filter(
      (p) =>
        p.available &&
        !isPetProduct(p) &&
        !isTestingKit(p) &&
        !isExcluded(p) &&
        !isGlassBottle(p) &&
        rankMap.has(p.handle.toLowerCase())
    );

    if (applyFormatFilters && formatFilters.length > 0) {
      filtered = filtered.filter((p) => {
        const format = getProductFormat(p);
        return format !== null && formatFilters.includes(format);
      });
    }

    return filtered
      .map((p) => scoreProduct(p, rankMap, primaryGoalId))
      .filter(Boolean)
      .sort((a, b) => b.score - a.score);
  }

  function getAvailableFormats(products, goals, state) {
    const scored = getScoredCandidates(products, goals, state, false);
    if (!scored) return new Set();

    const formats = new Set();
    scored
      .filter((p) => !isBundleProduct(p))
      .forEach((p) => {
        const format = getProductFormat(p);
        if (format && format !== 'bundle') formats.add(format);
      });
    return formats;
  }

  function buildStackFromScored(scored, maxSize) {
    const coveredGoals = new Set();
    const stack = [];
    const stackHandles = new Set();

    function tryAddProduct(product) {
      const handle = product.handle.toLowerCase();
      if (stackHandles.has(handle)) return false;

      stack.push(product);
      stackHandles.add(handle);
      product.matchedGoalIds.forEach((g) => coveredGoals.add(g));
      return true;
    }

    for (const product of scored) {
      if (stack.length >= maxSize) break;

      const newCoverage = product.matchedGoalIds.filter(
        (g) => !coveredGoals.has(g)
      );

      if (newCoverage.length === 0) continue;

      tryAddProduct(product);
    }

    for (const product of scored) {
      if (stack.length >= maxSize) break;
      tryAddProduct(product);
    }

    return stack;
  }

  function generateIndividualStack(products, goals, state) {
    const scored = getScoredCandidates(products, goals, state, true);
    if (!scored) return [];

    const individuals = dedupeByFamily(
      scored.filter((p) => !isBundleProduct(p))
    );

    return buildStackFromScored(individuals, DISPLAY_MAX);
  }

  function generateBundleStack(products, goals, state) {
    const scored = getScoredCandidates(products, goals, state, false);
    if (!scored) return [];

    const primaryGoalId = state.selectedGoalIds[0];
    const bundles = scored.filter(
      (p) =>
        isBundleProduct(p) && p.matchedGoalIds.includes(primaryGoalId)
    );

    return buildStackFromScored(bundles, BUNDLE_DISPLAY_MAX);
  }

  function getTestingKits(products, goals, state) {
    const { selectedGoalIds } = state;
    if (!selectedGoalIds.length) return [];

    const selectedGoals = selectedGoalIds
      .map((id) => goals.find((g) => g.id === id))
      .filter(Boolean);
    const primaryGoalId = selectedGoals[0]?.id;

    return products
      .filter(
        (p) => !isPetProduct(p) && isTestingKit(p) && isProductAvailable(p)
      )
      .map((p) => {
        let score = 0;
        const matchedGoalIds = [];

        selectedGoals.forEach((goal, index) => {
          if (testingKitMatchesGoal(p, goal)) {
            score += 5;
            matchedGoalIds.push(goal.id);
            if (index === 0) score += 3;
          }
        });

        return { ...p, score, matchedGoalIds, isBundle: false };
      })
      .filter(
        (p) =>
          p.score > 0 &&
          primaryGoalId &&
          p.matchedGoalIds.includes(primaryGoalId)
      )
      .sort((a, b) => b.score - a.score)
      .slice(0, 1);
  }

  function jsonCartFetchConfig(body) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    };
  }

  async function addItemsToCart(lineItems) {
    const response = await fetch(
      window.theme.routes.cart_add_url,
      jsonCartFetchConfig({
        items: lineItems,
        sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
        sections_url: window.location.pathname,
      })
    );
    const data = await response.json();

    if (data.status) {
      throw new Error(data.description || window.theme.strings.cartAddError);
    }

    return data;
  }

  function cartUpdateUrl() {
    const root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    return root.replace(/\/$/, '') + '/cart/update.js';
  }

  async function updateCartAttributes(attributes) {
    try {
      await fetch(cartUpdateUrl(), jsonCartFetchConfig({ attributes }));
    } catch (err) {
      /* attribution is best-effort; never block checkout */
    }
  }

  class HealthPlanBuilder {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(
        (root.closest('section') || root.parentElement).querySelector(
          '[data-hpb-config]'
        ).textContent
      );
      this.products = dedupeProducts(this.config.products);
      this.goals = this.config.goals.map((g) => ({
        ...g,
        is_digestion_goal: g.is_digestion_goal === true,
        product_handles: normalizeHandles(g.product_handles),
        reflux_handles: normalizeHandles(g.reflux_handles),
        general_digestion_handles: normalizeHandles(g.general_digestion_handles),
      }));
      this.settings = this.config.settings;
      this.displayStackByHandle = new Map();
      this.stackDisplayOrder = [];
      this.bundleStackByHandle = new Map();
      this.bundleDisplayOrder = [];

      this.state = {
        selectedGoalIds: [],
        digestionBranch: null,
        formatFilters: [],
        planType: 'comprehensive',
        selectedKitHandles: new Set(),
        selectedBundleHandles: new Set(),
        bundleSwappedHandles: new Map(),
        selectedVariants: new Map(),
        includedProductHandles: new Set(),
        checkoutLoading: false,
        errorMessage: null,
      };

      this._lastViewItemListFingerprint = null;

      this.bindEvents();
      this.render();
      this.trackBuilderStart();
    }

    get digestionGoal() {
      return this.goals.find((g) => g.is_digestion_goal);
    }

    needsDigestionBranch() {
      const dg = this.digestionGoal;
      return (
        dg &&
        this.state.selectedGoalIds.includes(dg.id) &&
        !this.state.digestionBranch
      );
    }

    rebuildDisplayStack() {
      const bundleFilterIdx = this.state.formatFilters.indexOf('bundle');
      if (bundleFilterIdx >= 0) {
        this.state.formatFilters.splice(bundleFilterIdx, 1);
      }

      const availableFormats = getAvailableFormats(
        this.products,
        this.goals,
        this.state
      );
      this.state.formatFilters = this.state.formatFilters.filter((f) =>
        availableFormats.has(f)
      );

      const individuals = generateIndividualStack(
        this.products,
        this.goals,
        this.state
      );
      const bundles = generateBundleStack(this.products, this.goals, this.state);

      this.displayStackByHandle = new Map(
        individuals.map((product) => [product.handle, product])
      );
      this.stackDisplayOrder = individuals.map((product) => product.handle);

      this.bundleStackByHandle = new Map(
        bundles.map((product) => [product.handle, product])
      );
      this.bundleDisplayOrder = bundles.map((product) => product.handle);

      this.applyPlanInclusions();

      this.maybeTrackViewItemList();
      this.maybeTrackEmptyFormatFallback();
    }

    getIndividualDisplayLimit() {
      return PLAN_INCLUDED_LIMITS[this.state.planType];
    }

    getVisibleStackHandles() {
      return this.stackDisplayOrder.slice(0, this.getIndividualDisplayLimit());
    }

    getGoalsStorageKey() {
      const goals = [...this.state.selectedGoalIds].sort().join(',');
      return `${goals}|${this.state.digestionBranch || ''}`;
    }

    loadIncludedStorage() {
      try {
        const raw = localStorage.getItem(INCLUDED_STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== 'object') return null;
        if (!Array.isArray(data.included) || !Array.isArray(data.known)) {
          return null;
        }
        return data;
      } catch (err) {
        return null;
      }
    }

    persistIncludedStorage() {
      try {
        if (!this.state.selectedGoalIds.length) {
          localStorage.removeItem(INCLUDED_STORAGE_KEY);
          return;
        }

        const goalsKey = this.getGoalsStorageKey();
        const existing = this.loadIncludedStorage();
        const visibleHandles = this.getVisibleStackHandles();
        const visibleSet = new Set(visibleHandles);
        const known = new Set(
          existing && existing.goalsKey === goalsKey ? existing.known : []
        );
        const included = new Set(this.state.includedProductHandles);

        visibleHandles.forEach((handle) => known.add(handle));
        included.forEach((handle) => known.add(handle));

        if (existing && existing.goalsKey === goalsKey) {
          existing.known.forEach((handle) => known.add(handle));
          existing.included.forEach((handle) => {
            if (!visibleSet.has(handle)) {
              included.add(handle);
              known.add(handle);
            }
          });
        }

        this.state.includedProductHandles = included;

        localStorage.setItem(
          INCLUDED_STORAGE_KEY,
          JSON.stringify({
            goalsKey,
            included: Array.from(included),
            known: Array.from(known),
          })
        );
      } catch (err) {
        /* storage is best-effort */
      }
    }

    applyPlanInclusions() {
      const visibleHandles = this.getVisibleStackHandles();
      const visibleSet = new Set(visibleHandles);
      const goalsKey = this.getGoalsStorageKey();
      const stored = this.loadIncludedStorage();

      if (!stored || stored.goalsKey !== goalsKey) {
        this.state.includedProductHandles = new Set(visibleHandles);
      } else {
        const known = new Set(stored.known);
        const storedIncluded = new Set(stored.included);
        const next = new Set();

        storedIncluded.forEach((handle) => {
          if (!visibleSet.has(handle)) next.add(handle);
        });

        visibleHandles.forEach((handle) => {
          if (!known.has(handle) || storedIncluded.has(handle)) {
            next.add(handle);
          }
        });

        this.state.includedProductHandles = next;
      }

      for (const handle of this.bundleDisplayOrder) {
        if (!this.state.selectedBundleHandles.has(handle)) continue;
        const product = this.bundleStackByHandle.get(handle);
        if (product) this.swapBundleComponents(product);
      }

      this.persistIncludedStorage();
    }

    getDisplayStack() {
      const limit = this.getIndividualDisplayLimit();
      return this.stackDisplayOrder
        .slice(0, limit)
        .map((handle) => this.displayStackByHandle.get(handle))
        .filter(Boolean);
    }

    getBundleStack() {
      return this.bundleDisplayOrder
        .map((handle) => this.bundleStackByHandle.get(handle))
        .filter(Boolean);
    }

    getTotalDisplayedCount() {
      return this.getDisplayStack().length + this.getBundleStack().length;
    }

    getIncludedStack() {
      const included = this.state.includedProductHandles;
      return this.getDisplayStack().filter((p) => included.has(p.handle));
    }

    getSelectedBundles() {
      return this.getBundleStack().filter((p) =>
        this.state.selectedBundleHandles.has(p.handle)
      );
    }

    swapBundleComponents(bundleProduct) {
      const variant = this.getSelectedVariant(bundleProduct);
      const components = this.getBundleComponents(bundleProduct, variant);
      if (!components.length) {
        this.state.bundleSwappedHandles.set(bundleProduct.handle, []);
        return;
      }

      const componentHandles = new Set(
        components.map((c) => c.handle.toLowerCase())
      );
      const swapped = [];

      for (const handle of this.stackDisplayOrder) {
        if (
          componentHandles.has(handle.toLowerCase()) &&
          this.state.includedProductHandles.has(handle)
        ) {
          this.state.includedProductHandles.delete(handle);
          swapped.push(handle);
        }
      }

      this.state.bundleSwappedHandles.set(bundleProduct.handle, swapped);
    }

    restoreBundleComponents(bundleHandle) {
      const swapped = this.state.bundleSwappedHandles.get(bundleHandle);
      if (!swapped) return;

      swapped.forEach((handle) => {
        this.state.includedProductHandles.add(handle);
      });
      this.state.bundleSwappedHandles.delete(bundleHandle);
    }

    findProductByHandle(handle) {
      return (
        this.displayStackByHandle.get(handle) ||
        this.bundleStackByHandle.get(handle)
      );
    }

    getKits() {
      if (!this.settings.show_testing_section) return [];
      return getTestingKits(this.products, this.goals, this.state);
    }

    getAvailableVariants(product) {
      const variants = product.variants || [];
      if (variants.length) {
        return variants.filter((v) => v.available !== false);
      }
      return [
        {
          id: product.variant_id,
          title: product.title,
          price: product.price,
          compare_at_price: product.compare_at_price,
          available: product.available,
        },
      ];
    }

    getSelectedVariant(product) {
      const variants = this.getAvailableVariants(product);
      const selectedId =
        this.state.selectedVariants.get(product.handle) ?? product.variant_id;
      const match = variants.find((v) => v.id === selectedId);
      if (match) return match;

      const fallback =
        variants.find((v) => v.id === product.variant_id) || variants[0];
      return (
        fallback || {
          id: product.variant_id,
          title: product.title,
          price: product.price,
          compare_at_price: product.compare_at_price,
          available: product.available,
        }
      );
    }

    getBundleComponents(product, variant) {
      const variantEntry = (product.variants || []).find(
        (v) => v.id === variant.id
      );
      if (variantEntry?.bundle_components?.length) {
        return variantEntry.bundle_components;
      }

      return getBundleComponentHandles(product)
        .map((h) =>
          this.products.find((p) => p.handle.toLowerCase() === h)
        )
        .filter(Boolean)
        .map((p) => ({ handle: p.handle, title: p.title }));
    }

    getBundleOriginalCents(product, variant) {
      const components = this.getBundleComponents(product, variant);

      if (components.length) {
        let sum = 0;
        let resolved = 0;

        for (const component of components) {
          const compProduct = this.products.find(
            (p) => p.handle.toLowerCase() === component.handle.toLowerCase()
          );
          if (compProduct) {
            const compVariant = this.getSelectedVariant(compProduct);
            if (compVariant?.price) {
              sum += compVariant.price;
              resolved++;
            }
          }
        }

        if (resolved === components.length && sum > 0) {
          return sum;
        }
      }

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        return variant.compare_at_price;
      }

      return null;
    }

    getBundleSavings(product, variant) {
      const price = variant.price;
      const original = this.getBundleOriginalCents(product, variant);

      if (!original || original <= price) {
        return { original: null, price, percent: 0 };
      }

      const percent = Math.round(((original - price) / original) * 100);
      return { original, price, percent };
    }

    renderBundleIncludes(product, variant) {
      if (!isBundleProduct(product)) return '';

      const components = this.getBundleComponents(product, variant);
      if (!components.length) return '';

      const titles = components.map((c) => c.title).join(', ');
      return `
        <p class="hpb-card__includes">
          <span class="hpb-card__includes-label">Includes:</span>
          ${titles}
        </p>
      `;
    }

    syncVariantSelections(products) {
      const activeHandles = new Set(products.map((p) => p.handle));

      for (const handle of this.state.selectedVariants.keys()) {
        if (!activeHandles.has(handle)) {
          this.state.selectedVariants.delete(handle);
        }
      }

      products.forEach((product) => {
        if (!this.state.selectedVariants.has(product.handle)) {
          this.state.selectedVariants.set(product.handle, product.variant_id);
        }
      });
    }

    getMaxGoals() {
      const n = Number(this.settings.max_goals);
      return n > 0 ? n : Infinity;
    }

    getCurrentStep() {
      if (!this.state.selectedGoalIds.length) return 1;
      if (this.needsDigestionBranch()) return 2;
      const hasProducts =
        this.getDisplayStack().length > 0 || this.getBundleStack().length > 0;
      return hasProducts ? 3 : 2;
    }

    getTotalCents() {
      const stack = this.getIncludedStack();
      const bundles = this.getSelectedBundles();
      const kits = this.getKits().filter((k) =>
        this.state.selectedKitHandles.has(k.handle)
      );
      return (
        stack.reduce(
          (sum, p) => sum + this.getSelectedVariant(p).price,
          0
        ) +
        bundles.reduce(
          (sum, p) => sum + this.getSelectedVariant(p).price,
          0
        ) +
        kits.reduce((sum, k) => sum + this.getSelectedVariant(k).price, 0)
      );
    }

    getItemCount() {
      const stack = this.getIncludedStack();
      const bundles = this.getSelectedBundles();
      const kits = this.getKits().filter((k) =>
        this.state.selectedKitHandles.has(k.handle)
      );
      return stack.length + bundles.length + kits.length;
    }

    getSelectedGoals() {
      return this.state.selectedGoalIds
        .map((id) => this.goals.find((g) => g.id === id))
        .filter(Boolean);
    }

    getGoalContext() {
      const selectedGoals = this.getSelectedGoals();
      const slugs = HpbAnalytics.goalSlugList(selectedGoals);
      const format = this.state.formatFilters.length
        ? this.state.formatFilters.join(',')
        : 'any';
      return {
        primary_goal: slugs[0] || '',
        goals: slugs.join(','),
        goal_count: selectedGoals.length,
        plan_type: this.state.planType,
        format,
      };
    }

    buildEcommerceItem(product, index) {
      const variant = this.getSelectedVariant(product);
      const matchedGoal = (product.matchedGoalIds || [])
        .map((goalId) => this.goals.find((g) => g.id === goalId))
        .find(Boolean);
      const category = matchedGoal
        ? HpbAnalytics.goalSlug(matchedGoal)
        : product.product_type || '';

      const item = {
        item_id: product.handle,
        item_name: product.title,
        price: +(variant.price / 100).toFixed(2),
        index,
      };
      if (variant.title && variant.title !== 'Default Title') {
        item.item_variant = variant.title;
      }
      if (category) {
        item.item_category = category;
      }
      return item;
    }

    buildPlanItems() {
      const stack = this.getIncludedStack();
      const bundles = this.getSelectedBundles();
      const kits = this.getKits().filter((k) =>
        this.state.selectedKitHandles.has(k.handle)
      );
      return [...stack, ...bundles, ...kits].map((product, index) =>
        this.buildEcommerceItem(product, index)
      );
    }

    getPlanValue() {
      return +(this.getTotalCents() / 100).toFixed(2);
    }

    trackBuilderStart() {
      HpbAnalytics.push(
        'builder_start',
        Object.assign(HpbAnalytics.getAttribution(), {
          primary_goal: '',
          goals: '',
          plan_type: this.state.planType,
          format: 'any',
        })
      );
    }

    trackSelectGoal(goalId, selected) {
      const goal = this.goals.find((g) => g.id === goalId);
      if (!goal) return;
      HpbAnalytics.push('select_goal', {
        goal: HpbAnalytics.goalSlug(goal),
        is_primary: this.state.selectedGoalIds[0] === goalId,
        goal_count: this.state.selectedGoalIds.length,
        selected,
      });
    }

    maybeTrackViewItemList() {
      if (this.needsDigestionBranch()) return;

      const displayStack = this.getDisplayStack();
      const bundles = this.getBundleStack();
      if (!displayStack.length && !bundles.length) return;

      const fingerprint = JSON.stringify({
        goals: this.state.selectedGoalIds,
        planType: this.state.planType,
        formats: this.state.formatFilters,
        branch: this.state.digestionBranch,
        handles: [...displayStack, ...bundles].map((p) => p.handle),
      });
      if (fingerprint === this._lastViewItemListFingerprint) return;
      this._lastViewItemListFingerprint = fingerprint;

      const items = [...displayStack, ...bundles].map((product, index) =>
        this.buildEcommerceItem(product, index)
      );
      const context = this.getGoalContext();

      HpbAnalytics.push('view_item_list', {
        item_list_name: ITEM_LIST_NAME,
        plan_value: this.getPlanValue(),
        goal_count: context.goal_count,
        primary_goal: context.primary_goal,
        goals: context.goals,
        items,
      });
    }

    trackRemoveStackItem(handle) {
      const product = this.findProductByHandle(handle);
      if (!product) return;
      const matchedGoal = (product.matchedGoalIds || [])
        .map((goalId) => this.goals.find((g) => g.id === goalId))
        .find(Boolean);
      HpbAnalytics.push('remove_stack_item', {
        item_id: product.handle,
        item_name: product.title,
        goal: matchedGoal ? HpbAnalytics.goalSlug(matchedGoal) : '',
      });
    }

    maybeTrackEmptyFormatFallback() {
      if (this.needsDigestionBranch()) return;
      if (!this.state.formatFilters.length) return;
      if (this.getDisplayStack().length) return;

      const unfilteredState = Object.assign({}, this.state, {
        formatFilters: [],
      });
      const candidates = getScoredCandidates(
        this.products,
        this.goals,
        unfilteredState,
        false
      );
      if (!candidates || !candidates.length) return;

      HpbAnalytics.push('empty_format_fallback', {
        format: this.state.formatFilters.join(','),
        goals: this.getGoalContext().goals,
      });
    }

    bindEvents() {
      this.root.addEventListener('change', (e) => {
        const includeCheckbox = e.target.closest('[data-hpb-include]');
        if (includeCheckbox) {
          const handle = includeCheckbox.dataset.hpbInclude;
          if (includeCheckbox.checked) {
            this.state.includedProductHandles.add(handle);
          } else {
            this.state.includedProductHandles.delete(handle);
            this.trackRemoveStackItem(handle);
          }
          this.persistIncludedStorage();
          this.render({ rebuildStack: false });
          return;
        }

        const variantSelect = e.target.closest('[data-hpb-variant]');
        if (!variantSelect) return;

        const handle = variantSelect.dataset.hpbVariant;
        this.state.selectedVariants.set(handle, Number(variantSelect.value));

        if (this.state.selectedBundleHandles.has(handle)) {
          this.restoreBundleComponents(handle);
          const product = this.bundleStackByHandle.get(handle);
          if (product) this.swapBundleComponents(product);
          this.persistIncludedStorage();
        }

        this.render();
      });

      this.root.addEventListener('click', (e) => {
        const bundlesLink = e.target.closest('[data-hpb-bundles-link]');
        if (bundlesLink) {
          e.preventDefault();
          const section = this.root.querySelector('#hpb-bundles');
          section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        const goalBtn = e.target.closest('[data-hpb-goal]');
        if (goalBtn) {
          this.toggleGoal(goalBtn.dataset.hpbGoal);
          return;
        }

        const formatBtn = e.target.closest('[data-hpb-format]');
        if (formatBtn) {
          this.toggleFormat(formatBtn.dataset.hpbFormat);
          return;
        }

        const planBtn = e.target.closest('[data-hpb-plan]');
        if (planBtn) {
          this.state.planType = planBtn.dataset.hpbPlan;
          this.applyPlanInclusions();
          HpbAnalytics.push('select_plan_type', {
            plan_type: this.state.planType,
          });
          this.render({ rebuildStack: false });
          return;
        }

        const branchBtn = e.target.closest('[data-hpb-digestion-branch]');
        if (branchBtn) {
          this.state.digestionBranch = branchBtn.dataset.hpbDigestionBranch;
          this.render({ rebuildStack: true });
          return;
        }

        const kitBtn = e.target.closest('[data-hpb-kit-toggle]');
        if (kitBtn) {
          this.toggleKit(kitBtn.dataset.hpbKitToggle);
          return;
        }

        const bundleBtn = e.target.closest('[data-hpb-bundle-toggle]');
        if (bundleBtn) {
          this.toggleBundle(bundleBtn.dataset.hpbBundleToggle);
          return;
        }

        const checkoutBtn = e.target.closest('[data-hpb-checkout]');
        if (checkoutBtn) {
          this.checkout();
        }
      });
    }

    toggleGoal(goalId) {
      const idx = this.state.selectedGoalIds.indexOf(goalId);
      const goal = this.goals.find((g) => g.id === goalId);
      let selected;

      if (idx >= 0) {
        this.state.selectedGoalIds.splice(idx, 1);
        selected = false;
        if (goal?.is_digestion_goal) {
          this.state.digestionBranch = null;
        }
      } else if (this.state.selectedGoalIds.length >= this.getMaxGoals()) {
        return;
      } else {
        this.state.selectedGoalIds.push(goalId);
        selected = true;
      }

      this.state.selectedKitHandles = new Set();
      this.state.selectedBundleHandles = new Set();
      this.state.bundleSwappedHandles = new Map();
      this.state.errorMessage = null;
      this.trackSelectGoal(goalId, selected);
      this.render({ rebuildStack: true });
    }

    toggleFormat(format) {
      const available = getAvailableFormats(
        this.products,
        this.goals,
        this.state
      );
      if (!available.has(format)) return;

      const filters = this.state.formatFilters;
      const idx = filters.indexOf(format);
      let selected;
      if (idx >= 0) {
        filters.splice(idx, 1);
        selected = false;
      } else {
        filters.push(format);
        selected = true;
      }
      HpbAnalytics.push('select_format', { format, selected });
      this.render({ rebuildStack: true });
    }

    toggleKit(handle) {
      const set = this.state.selectedKitHandles;
      const kit = this.getKits().find((k) => k.handle === handle);
      const value = kit
        ? +(this.getSelectedVariant(kit).price / 100).toFixed(2)
        : 0;
      if (set.has(handle)) {
        set.delete(handle);
        HpbAnalytics.push('remove_testing', { item_id: handle, value });
      } else {
        set.add(handle);
        HpbAnalytics.push('add_testing', { item_id: handle, value });
      }
      this.render();
    }

    toggleBundle(handle) {
      const set = this.state.selectedBundleHandles;
      const product = this.bundleStackByHandle.get(handle);
      const variant = product ? this.getSelectedVariant(product) : null;
      const value = variant ? +(variant.price / 100).toFixed(2) : 0;
      const discountPct =
        product && variant
          ? this.getBundleSavings(product, variant).percent
          : 0;
      if (set.has(handle)) {
        set.delete(handle);
        this.restoreBundleComponents(handle);
        HpbAnalytics.push('remove_bundle', {
          bundle_id: handle,
          value,
          discount_pct: discountPct,
        });
      } else {
        set.add(handle);
        if (product) this.swapBundleComponents(product);
        HpbAnalytics.push('add_bundle', {
          bundle_id: handle,
          value,
          discount_pct: discountPct,
        });
      }
      this.persistIncludedStorage();
      this.render();
    }

    async checkout() {
      if (this.state.checkoutLoading) return;

      const stack = this.getIncludedStack();
      const bundles = this.getSelectedBundles();
      const kits = this.getKits().filter((k) =>
        this.state.selectedKitHandles.has(k.handle)
      );
      const items = [...stack, ...bundles, ...kits];

      if (!items.length) return;

      this.state.checkoutLoading = true;
      this.state.errorMessage = null;
      this.render();

      const context = this.getGoalContext();
      const lineProperties = {
        _source: ATTR_SOURCE_VALUE,
        _hpb_goals: context.goals,
      };
      const lineItems = items.map((item) => ({
        id: this.getSelectedVariant(item).id,
        quantity: 1,
        properties: lineProperties,
      }));

      try {
        const data = await addItemsToCart(lineItems);

        await updateCartAttributes({
          source: ATTR_SOURCE_VALUE,
          hpb_goals: context.goals,
          hpb_plan_type: context.plan_type,
          hpb_format: context.format,
        });

        HpbAnalytics.push(
          'add_to_cart',
          Object.assign(
            {
              value: this.getPlanValue(),
              items: this.buildPlanItems(),
            },
            {
              primary_goal: context.primary_goal,
              goals: context.goals,
              plan_type: context.plan_type,
              format: context.format,
              builder_assisted: true,
            }
          )
        );

        if (
          window.theme.settings &&
          window.theme.settings.cartType === 'drawer'
        ) {
          document.body.dispatchEvent(
            new CustomEvent('baseline:cart:afteradditem', {
              bubbles: true,
              detail: { response: data },
            })
          );
          document.dispatchEvent(new CustomEvent('theme:open:cart-drawer'));
          this.state.checkoutLoading = false;
          this.render();
        } else {
          window.location.href = window.theme.routes.cart_url;
        }
      } catch (err) {
        this.state.checkoutLoading = false;
        this.state.errorMessage =
          err.message || window.theme.strings.cartError;
        this.render();
      }
    }

    renderTimeline() {
      const step = this.getCurrentStep();
      const labels = [
        this.settings.step_1_label,
        this.settings.step_2_label,
        this.settings.step_3_label,
      ];

      return `
        <div class="hpb-timeline" role="navigation" aria-label="Progress">
          ${labels
            .map((label, i) => {
              const num = i + 1;
              const active = num <= step;
              const connector =
                i < labels.length - 1
                  ? `<div class="hpb-timeline__connector${num < step ? ' hpb-timeline__connector--active' : ''}" aria-hidden="true"></div>`
                  : '';
              return `
                <div class="hpb-timeline__step">
                  <div class="hpb-timeline__dot${active ? ' hpb-timeline__dot--active' : ''}">${num}</div>
                  <span class="hpb-timeline__label${active ? ' hpb-timeline__label--active' : ''}">${label}</span>
                </div>
                ${connector}
              `;
            })
            .join('')}
        </div>
      `;
    }

    renderGoalsLimitHint() {
      const maxGoals = Number(this.settings.max_goals);
      if (!maxGoals) return '';

      return `<p class="hpb-goals-section__hint">Choose up to ${maxGoals} goal${maxGoals === 1 ? '' : 's'}.</p>`;
    }

    renderGoalTile(goal) {
      const selected = this.state.selectedGoalIds.includes(goal.id);
      const isPrimary =
        this.state.selectedGoalIds[0] === goal.id && selected;
      const atLimit =
        !selected &&
        this.state.selectedGoalIds.length >= this.getMaxGoals();
      const textColor = goal.tile_text_color || '#ffffff';

      const iconHtml = goal.icon
        ? `<img src="${goal.icon}" alt="" class="hpb-goal__icon" width="48" height="48" loading="lazy">`
        : `<span class="hpb-goal__icon-placeholder" aria-hidden="true"></span>`;

      return `
        <button
          type="button"
          class="hpb-goal${selected ? ' hpb-goal--selected' : ''}${atLimit ? ' hpb-goal--disabled' : ''}"
          style="--hpb-tile-bg: ${goal.tile_color}; --hpb-tile-text: ${textColor};"
          data-hpb-goal="${goal.id}"
          aria-pressed="${selected}"
          ${atLimit ? 'disabled aria-disabled="true"' : ''}
        >
          ${isPrimary ? '<span class="hpb-goal__primary">Primary</span>' : ''}
          ${selected ? '<span class="hpb-goal__check" aria-hidden="true">✓</span>' : ''}
          ${iconHtml}
          <span class="hpb-goal__label">${goal.label}</span>
          <span class="hpb-goal__desc">${goal.description}</span>
        </button>
      `;
    }

    renderDigestionBranch() {
      if (!this.needsDigestionBranch()) return '';

      return `
        <div class="hpb-digestion-branch" role="group" aria-labelledby="hpb-digestion-heading">
          <h3 id="hpb-digestion-heading" class="hpb-section-heading">What kind of digestive support?</h3>
          <p class="hpb-digestion-branch__hint">Choose one so we can recommend the right products.</p>
          <div class="hpb-digestion-branch__options">
            <button type="button" class="hpb-digestion-branch__btn" data-hpb-digestion-branch="reflux">
              Reflux &amp; heartburn
            </button>
            <button type="button" class="hpb-digestion-branch__btn" data-hpb-digestion-branch="general">
              General digestion
            </button>
          </div>
        </div>
      `;
    }

    renderFilters() {
      if (!this.state.selectedGoalIds.length || this.needsDigestionBranch()) {
        return '';
      }

      const { formatFilters, planType } = this.state;
      const availableFormats = getAvailableFormats(
        this.products,
        this.goals,
        this.state
      );

      const formatPills = FORMAT_FILTER_OPTIONS.map((opt) => {
        const active = formatFilters.includes(opt.value);
        const disabled = !availableFormats.has(opt.value);
        return `
          <button
            type="button"
            class="hpb-pill${active ? ' hpb-pill--active' : ''}${disabled ? ' hpb-pill--disabled' : ''}"
            data-hpb-format="${opt.value}"
            aria-pressed="${active}"
            ${disabled ? 'disabled aria-disabled="true"' : ''}
          >${opt.label}</button>
        `;
      }).join('');

      return `
        <div class="hpb-filters">
          <div class="hpb-filters__group">
            <span class="hpb-filters__label">Format</span>
            <div class="hpb-filters__pills">${formatPills}</div>
          </div>
          <div class="hpb-filters__group">
            <span class="hpb-filters__label">Plan type</span>
            <div class="hpb-plan-toggle">
              <button
                type="button"
                class="hpb-plan-toggle__btn${planType === 'minimal' ? ' hpb-plan-toggle__btn--active' : ''}"
                data-hpb-plan="minimal"
                aria-pressed="${planType === 'minimal'}"
              >Minimal (2 included)</button>
              <button
                type="button"
                class="hpb-plan-toggle__btn${planType === 'comprehensive' ? ' hpb-plan-toggle__btn--active' : ''}"
                data-hpb-plan="comprehensive"
                aria-pressed="${planType === 'comprehensive'}"
              >Comprehensive (4 included)</button>
            </div>
          </div>
        </div>
      `;
    }

    renderPlanToggle(handle, selected, toggleAttr) {
      if (!selected) {
        return `<button type="button" class="hpb-plan-toggle__add" ${toggleAttr}="${handle}">Add to Plan</button>`;
      }

      return `
        <div class="hpb-plan-toggle">
          <button type="button" class="hpb-plan-toggle__added" disabled aria-disabled="true">
            <span aria-hidden="true">✓</span> Added
          </button>
          <button type="button" class="hpb-plan-toggle__remove" ${toggleAttr}="${handle}">Remove</button>
        </div>
      `;
    }

    renderVariantSelect(product) {
      if (!product.variants?.length || product.variants.length <= 1) {
        return '';
      }

      const variants = this.getAvailableVariants(product);
      if (variants.length <= 1) return '';

      const selected = this.getSelectedVariant(product);

      const options = variants
        .map((variant) => {
          const label =
            variant.title === 'Default Title'
              ? product.title
              : variant.title;
          return `<option value="${variant.id}"${variant.id === selected.id ? ' selected' : ''}>${label} — ${formatMoney(variant.price)}</option>`;
        })
        .join('');

      return `
        <label class="hpb-card__variant">
          <span class="hpb-card__variant-label">Option</span>
          <span class="hpb-card__variant-wrap">
            <select class="hpb-card__variant-select" data-hpb-variant="${product.handle}" aria-label="Variant for ${product.title}">
              ${options}
            </select>
            <span class="hpb-card__variant-chevron" aria-hidden="true">${CHEVRON_DOWN_SVG}</span>
          </span>
        </label>
      `;
    }

    renderProductCard(product, options = {}) {
      const {
        showKitToggle = false,
        kitSelected = false,
        showBundleToggle = false,
        bundleSelected = false,
        showIncludeCheckbox = false,
      } = options;
      const included = this.state.includedProductHandles.has(product.handle);
      const variant = this.getSelectedVariant(product);
      const onSale =
        variant.compare_at_price && variant.compare_at_price > variant.price;
      const format = getProductFormat(product);
      const bundleProduct = isBundleProduct(product);
      const bundleSavings = bundleProduct
        ? this.getBundleSavings(product, variant)
        : null;

      const goalTags = (product.matchedGoalIds || [])
        .map((goalId) => this.goals.find((g) => g.id === goalId))
        .filter(Boolean)
        .map((g) => {
          const isPrimary = g.id === this.state.selectedGoalIds[0];
          return `<span class="hpb-card__goal-tag${isPrimary ? ' hpb-card__goal-tag--primary' : ''}">${g.label}</span>`;
        })
        .join('');

      const formatTag =
        format && format !== 'bundle'
          ? `<span class="hpb-card__format-tag">${format}</span>`
          : '';

      const priceHtml =
        bundleSavings?.original
          ? `<s class="hpb-card__price-original">${formatMoney(bundleSavings.original)}</s> ${formatMoney(variant.price)}`
          : formatMoney(variant.price);

      const kitBtn = showKitToggle
        ? this.renderPlanToggle(
            product.handle,
            kitSelected,
            'data-hpb-kit-toggle'
          )
        : '';

      const bundleBtn = showBundleToggle
        ? this.renderPlanToggle(
            product.handle,
            bundleSelected,
            'data-hpb-bundle-toggle'
          )
        : '';

      const bundleSaveBadge =
        showBundleToggle && bundleSavings?.percent > 0
          ? `<span class="pill-badge pill-badge--bundle pointer-events-none">Save ${bundleSavings.percent}%</span>`
          : '';

      const includeCheckbox = showIncludeCheckbox
        ? `
          <label class="hpb-card__include">
            <input
              type="checkbox"
              class="hpb-card__include-input"
              data-hpb-include="${product.handle}"
              ${included ? 'checked' : ''}
              aria-label="Include ${product.title} in plan"
            >
            <span class="hpb-card__include-circle" aria-hidden="true"></span>
          </label>
        `
        : '';

      return `
        <article class="hpb-card${showIncludeCheckbox && !included ? ' hpb-card--excluded' : ''}">
          ${includeCheckbox}
          <div class="hpb-card__image-wrap">
            <img src="${product.image}" alt="" class="hpb-card__image" width="80" height="80" loading="lazy">
          </div>
          <div class="hpb-card__body">
            <div class="hpb-card__header">
              <div class="hpb-card__title-block">
                <h3 class="hpb-card__title"><a href="${product.url}">${product.title}</a>${bundleSaveBadge}</h3>
                <span class="product-tile-reviews block" data-product-id="${product.id}" aria-hidden="true"></span>
              </div>
              <span class="hpb-card__price">${priceHtml}</span>
            </div>
            ${this.renderVariantSelect(product)}
            ${this.renderBundleIncludes(product, variant)}
            <div class="hpb-card__tags">
              ${onSale ? '<span class="pill-badge pill-badge--sale pointer-events-none">SAVE</span>' : ''}
              ${formatTag}
            </div>
            ${goalTags ? `<div class="hpb-card__goals">${goalTags}</div>` : ''}
            ${kitBtn}
            ${bundleBtn}
          </div>
        </article>
      `;
    }

    renderStackSummary() {
      const summary = buildStackSummary(this.state.selectedGoalIds, this.goals);
      if (!summary) return '';
      return `<p class="hpb-stack__summary">${summary}</p>`;
    }

    renderStackTrustLine() {
      return `<p class="hpb-stack__trust">Clean-label, USDA Organic, and made fresh in the USA.</p>`;
    }

    renderStackIntro() {
      const summary = this.renderStackSummary();
      const trustLine = this.renderStackTrustLine();
      return `
        <div class="hpb-stack__intro">
          <h2 class="hpb-section-heading">${this.settings.stack_heading}</h2>
          ${summary}
          ${trustLine}
        </div>
      `;
    }

    renderStack(stack) {
      if (!this.state.selectedGoalIds.length || this.needsDigestionBranch()) {
        return '';
      }

      const intro = this.renderStackIntro();
      const includedCount = this.getIncludedStack().length;
      const totalDisplayed = this.getTotalDisplayedCount();
      const hasBundles = this.getBundleStack().length > 0;

      const header = `
        <div class="hpb-stack__header">
          ${intro}
          <span class="hpb-stack__count">${includedCount} of ${totalDisplayed} included</span>
        </div>
      `;

      if (!stack.length) {
        if (hasBundles) {
          return `
            <section class="hpb-stack" aria-live="polite">
              ${header}
            </section>
          `;
        }

        return `
          <section class="hpb-stack" aria-live="polite">
            ${header}
            <div class="hpb-empty">
              <p class="hpb-empty__title">No matches yet</p>
              <p class="hpb-empty__text">Try adjusting your format filters or selecting different goals. If this persists, check that products are assigned in the goal block settings.</p>
            </div>
          </section>
        `;
      }

      const cards = stack
        .map((p) => this.renderProductCard(p, { showIncludeCheckbox: true }))
        .join('');

      return `
        <section class="hpb-stack" aria-live="polite">
          ${header}
          <div class="hpb-stack__grid">${cards}</div>
        </section>
      `;
    }

    renderBundleStack() {
      if (!this.state.selectedGoalIds.length || this.needsDigestionBranch()) {
        return '';
      }

      const bundles = this.getBundleStack();
      if (!bundles.length) return '';

      const bundleTitle = 'Bundle & Save';
      const primaryGoal = this.goals.find(
        (g) => g.id === this.state.selectedGoalIds[0]
      );
      const bundleSub = primaryGoal
        ? `Everything for your ${primaryGoal.label} goal in one set.`
        : this.settings.bundle_subheading;

      const cards = bundles
        .map((p) =>
          this.renderProductCard(p, {
            showBundleToggle: true,
            bundleSelected: this.state.selectedBundleHandles.has(p.handle),
          })
        )
        .join('');

      return `
        <section class="hpb-bundles" id="hpb-bundles" aria-live="polite">
          <div class="hpb-bundles__header">
            <h2 class="hpb-bundles__title">${bundleTitle}</h2>
            <p class="hpb-bundles__sub">${bundleSub}</p>
          </div>
          <div class="hpb-stack__grid hpb-stack__grid--full">${cards}</div>
        </section>
      `;
    }

    renderTestingKits() {
      if (
        !this.settings.show_testing_section ||
        !this.state.selectedGoalIds.length ||
        this.needsDigestionBranch()
      ) {
        return '';
      }

      const kits = this.getKits();
      if (!kits.length) return '';

      const cards = kits
        .map((kit) =>
          this.renderProductCard(kit, {
            showKitToggle: true,
            kitSelected: this.state.selectedKitHandles.has(kit.handle),
          })
        )
        .join('');

      return `
        <section class="hpb-testing">
          <div class="hpb-testing__header">
            <span class="hpb-testing__emoji" aria-hidden="true">🧪</span>
            <div>
              <h2 class="hpb-testing__title">Add at-home testing</h2>
              <p class="hpb-testing__sub">Track your progress with a test kit matched to your goals.</p>
            </div>
          </div>
          <div class="hpb-stack__grid hpb-stack__grid--full">${cards}</div>
        </section>
      `;
    }

    renderStickyFooter() {
      const stack = this.getDisplayStack();
      const bundles = this.getBundleStack();
      if ((!stack.length && !bundles.length) || this.needsDigestionBranch()) {
        return '';
      }

      const count = this.getItemCount();
      const total = formatMoney(this.getTotalCents());
      const loading = this.state.checkoutLoading;
      const error = this.state.errorMessage;
      const disabled = loading || count === 0;

      return `
        <div class="hpb-footer">
          <div class="hpb-footer__container">
            ${error ? `<p class="hpb-footer__error" role="alert">${error}</p>` : ''}
            <div class="hpb-footer__inner">
              <div>
                <p class="hpb-footer__label">Your Plan Total</p>
                <p class="hpb-footer__count">${count} item${count !== 1 ? 's' : ''}</p>
              </div>
              <span class="hpb-footer__total">${total}</span>
            </div>
            <button
              type="button"
              class="theme-button hpb-footer__cta"
              data-hpb-checkout
              ${disabled ? 'disabled' : ''}
            >${loading ? 'Adding…' : this.settings.cta_text}</button>
          </div>
        </div>
      `;
    }

    renderTrustBadges() {
      if (!this.settings.show_trust_badges || !this.settings.trust_badges_image) {
        return '';
      }

      return `
        <div class="hpb-trust">
          <img
            src="${this.settings.trust_badges_image}"
            alt="Trust badges"
            class="hpb-trust__image"
            loading="lazy"
            width="600"
            height="60"
          >
        </div>
      `;
    }

    render(options = {}) {
      const { rebuildStack = false } = options;

      if (rebuildStack) {
        this.rebuildDisplayStack();
      }

      const hasGoals = this.state.selectedGoalIds.length > 0;
      const stack = this.getDisplayStack();
      const bundles = this.getBundleStack();
      const kits = this.getKits();
      this.syncVariantSelections([...stack, ...bundles, ...kits]);

      this.root.innerHTML = `
        <div class="hpb-inner">
          <header class="hpb-hero">
            <h1 class="hpb-hero__title font-heading">${this.settings.heading}</h1>
            <p class="hpb-hero__sub">${this.settings.subheading}</p>
          </header>

          ${this.renderTrustBadges()}

          ${this.renderTimeline()}

          <section class="hpb-goals-section">
            <h2 class="hpb-section-heading">${this.settings.goals_heading}</h2>
            ${this.renderGoalsLimitHint()}
            <div class="hpb-goals">${this.goals.map((g) => this.renderGoalTile(g)).join('')}</div>
          </section>

          ${hasGoals ? this.renderDigestionBranch() : ''}
          ${hasGoals ? `<div class="hpb-filters-wrap">${this.renderFilters()}</div>` : ''}
          ${this.renderStack(stack)}
          ${this.renderBundleStack()}
          ${this.renderTestingKits()}
          ${this.renderStickyFooter()}
        </div>
      `;
    }
  }

  function init() {
    document.querySelectorAll('[data-hpb-root]').forEach((root) => {
      if (root.dataset.hpbInitialized) return;
      root.dataset.hpbInitialized = 'true';
      new HealthPlanBuilder(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
